'use client';

import { parsePhoneValue } from '@/utils/tools';
import { omit } from 'lodash-es';
import { useTranslations } from '@/i18n/use-translations';
import React from 'react';
import { Control, useController } from 'react-hook-form';
import OriginalPhoneInput, { OriginalPhoneInputProps } from './PhoneInput';

export type PhoneInputProps = OriginalPhoneInputProps & {
  name: string;
  control: Control<any>;
  /**
   * 是否必填，默认 true
   */
  required?: boolean;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  control,
  telCode,
  required = true,
  countryList,
  ...restProps
}) => {
  const t = useTranslations('components.form.phone');

  // 根据 required 参数决定是否使用校验规则
  const validationRules = !required
    ? {}
    : {
        validate: (value) => {
          const { telephone, telCode: parseTelCode } = parsePhoneValue(value);
          // 无区号兼容老数据
          if (!parseTelCode) {
            return t('validation.regionCodeRequired');
          }
          // 纯数字的电话号码
          const pureNumTelphone = telephone.replace(/\D/g, '');
          const len = telephone.length as number;

          if (pureNumTelphone.length === 0) {
            return t('validation.phoneRequired');
          }
          const countryId = parseTelCode.split('(')[0];

          if (countryId === 'US') {
            if (
              +pureNumTelphone.length !== 10 ||
              +len > 13 ||
              (+len === 13 && !/^\(\d{3}\)\d{3}-\d{4}$/.test(telephone))
            ) {
              return t('validation.phoneInvalid');
            }
            if (!/^[^01].*/.test(pureNumTelphone)) {
              return t('validation.phoneFirstDigit');
            }
          }
          return true;
        },
      };

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationRules,
  });

  return (
    <OriginalPhoneInput
      {...omit(restProps, ['name', 'control', 'required'])}
      countryList={countryList}
      telCode={telCode}
      onChange={(newValue) => {
        // 会不会触发校验，取决于onChange的值是否变化
        field.onChange(newValue);
      }}
      value={field.value}
      onBlur={field.onBlur}
      error={!!error}
      helperText={error ? error.message : restProps.helperText}
    />
  );
};

export default PhoneInput;
